import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Gift, Calendar, Users, Heart, ChevronRight, AlertCircle, DollarSign } from 'react-feather';
import { useCouple } from '../../contexts/CoupleContext';
import { useTheme } from '../../contexts/ThemeContext';
import VisitorLayout from '../../components/layouts/VisitorLayout';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Loader from '../../components/common/Loader';
import PhotoService from '../../services/photo.service';
import EventService from '../../services/event.service';
import UploadService from '../../services/upload.service';
import { Couple, Photo, Event } from '../../types';
import { motion, useScroll, useTransform, easeOut } from 'framer-motion';

// Add global smooth scrolling
const style = document.createElement('style');
style.textContent = `
  html {
    scroll-behavior: smooth;
  }
`;
document.head.appendChild(style);

const WeddingHome: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const { getCoupleByCustomUrl, loading: coupleLoading, error: coupleError } = useCouple();
  const { themeClasses, darkMode } = useTheme();

  const [couple, setCouple] = useState<Couple | null>(null);
  const [featuredPhotos, setFeaturedPhotos] = useState<Photo[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  // Parallax effect with correct easing function
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, 50], { ease: easeOut });
  const storyY = useTransform(scrollY, [300, 600], [0, 50], { ease: easeOut });
  const photosY = useTransform(scrollY, [600, 900], [0, 50], { ease: easeOut });
  const eventsY = useTransform(scrollY, [900, 1200], [0, 50], { ease: easeOut });
  const ctaY = useTransform(scrollY, [1200, 1500], [0, 50], { ease: easeOut });

  useEffect(() => {
    if (customUrl) {
      fetchCoupleData();
    }
  }, [customUrl]);

  const fetchCoupleData = async () => {
    if (!customUrl) return;

    setLoading(true);
    setError(null);

    try {
      const coupleData = await getCoupleByCustomUrl(customUrl);
      setCouple(coupleData);

      const [photos, events] = await Promise.all([
        PhotoService.getCouplePhotos(coupleData._id, true),
        EventService.getCoupleEvents(coupleData._id),
      ]);

      setFeaturedPhotos(photos);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const future = events
        .filter((event) => new Date(event.date) >= today)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setUpcomingEvents(future);

      setFailedImages({});
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load wedding details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayUrl = (url: string): string => {
    return UploadService.getOptimizedImageUrl(url);
  };

  const handleImageError = (photoId: string) => {
    setFailedImages((prev) => ({
      ...prev,
      [photoId]: true,
    }));
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const calculateDaysUntilWedding = (weddingDate: string): number => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const wedding = new Date(weddingDate);
    wedding.setHours(0, 0, 0, 0);

    const diffTime = wedding.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  const getBackgroundPhotos = () => {
    if (featuredPhotos.length === 0) {
      return {
        hero: '',
        story: '',
        photos: '',
        events: '',
        cta: '',
      };
    }

    // Shuffle photos for variety
    const shuffled = [...featuredPhotos].sort(() => 0.5 - Math.random());

    // Select distinct photos for each section
    return {
      hero: getDisplayUrl(couple!.coverPhoto || shuffled[0]?.url || ''),
      story: getDisplayUrl(shuffled[1]?.url || shuffled[0]?.url || ''),
      photos: getDisplayUrl(shuffled[2]?.url || shuffled[0]?.url || ''),
      events: getDisplayUrl(shuffled[3]?.url || shuffled[0]?.url || ''),
      cta: getDisplayUrl(shuffled[4]?.url || shuffled[0]?.url || ''),
    };
  };

  const backgroundPhotos = getBackgroundPhotos();

  if (loading || coupleLoading) {
    return <Loader fullScreen label="Loading wedding details..." />;
  }

  if (error || coupleError) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          darkMode ? 'bg-gray-900' : 'bg-ivory-50'
        } px-4`}
      >
        <div className="text-center">
          <Heart className="mx-auto h-12 w-12 text-rose-500" />
          <h2 className="mt-2 text-2xl font-playfair text-gray-900 dark:text-white">Wedding Not Found</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error || coupleError}</p>
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-lora rounded-full shadow-md bg-rose-500 text-white hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!couple) {
    return null;
  }

  const daysUntilWedding = calculateDaysUntilWedding(couple.weddingDate);

  return (
    <VisitorLayout couple={couple}>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <motion.div className="absolute inset-0 z-0" style={{ y: heroY }}>
          {backgroundPhotos.hero && !failedImages['hero'] ? (
            <img
              className="w-full h-full object-cover transition-opacity duration-500 ease-out"
              src={backgroundPhotos.hero}
              alt={`${couple.names} wedding`}
              onError={() => handleImageError('hero')}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-rose-300 to-navy-900 transition-colors duration-500 ease-out"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent transition-opacity duration-500 ease-out"></div>
        </motion.div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="text-5xl sm:text-6xl lg:text-7xl font-playfair text-white tracking-wide"
          >
            {couple.names}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
            className="mt-6 text-xl sm:text-2xl font-lora text-ivory-100 max-w-3xl mx-auto"
          >
            We're getting married on {formatDate(couple.weddingDate)}
            {couple.weddingLocation && <span> at {couple.weddingLocation}</span>}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: 'easeOut' }}
            className="mt-10"
          >
            {daysUntilWedding > 0 ? (
              <div className="inline-flex items-center px-8 py-4 bg-ivory-100/90 backdrop-blur-sm rounded-full shadow-lg">
                <Heart className="h-6 w-6 mr-3 text-rose-500" />
                <span className="text-lg font-lora text-gray-900">
                  {daysUntilWedding} days until we say "I do"
                </span>
              </div>
            ) : daysUntilWedding === 0 ? (
              <div className="inline-flex items-center px-8 py-4 bg-ivory-100/90 backdrop-blur-sm rounded-full shadow-lg">
                <Heart className="h-6 w-6 mr-3 text-rose-500" />
                <span className="text-lg font-lora text-gray-900">Today is our wedding day!</span>
              </div>
            ) : (
              <div className="inline-flex items-center px-8 py-4 bg-ivory-100/90 backdrop-blur-sm rounded-full shadow-lg">
                <Heart className="h-6 w-6 mr-3 text-rose-500" />
                <span className="text-lg font-lora text-gray-900">We are happily married!</span>
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>

      {/* Our Story Section */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative py-24 px-4 sm:px-6 lg:px-8 bg-ivory-50 dark:bg-navy-900"
      >
        <motion.div className="absolute inset-0 z-0" style={{ y: storyY }}>
          {backgroundPhotos.story && !failedImages['story'] ? (
            <img
              className="w-full h-full object-cover opacity-30 transition-opacity duration-500 ease-out"
              src={backgroundPhotos.story}
              alt="Our Story background"
              onError={() => handleImageError('story')}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-ivory-100 to-rose-100 dark:from-navy-800 dark:to-gray-900 transition-colors duration-500 ease-out"></div>
          )}
          <div className="absolute inset-0 bg-ivory-50/80 dark:bg-navy-900/80 transition-opacity duration-500 ease-out"></div>
        </motion.div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-4xl font-playfair text-gray-900 dark:text-white"
          >
            Our Story
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 prose prose-lg font-lora text-gray-700 dark:text-gray-200 mx-auto"
          >
            {couple.story ? (
              <div
                dangerouslySetInnerHTML={{ __html: couple.story.replace(/\n/g, '<br />') }}
              />
            ) : (
              <p className="italic text-gray-500 dark:text-gray-400">
                Our story is being written, one moment at a time...
              </p>
            )}
          </motion.div>
        </div>
      </motion.div>

      {/* Featured Photos */}
      {featuredPhotos.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="relative py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800"
        >
          <motion.div className="absolute inset-0 z-0" style={{ y: photosY }}>
            {backgroundPhotos.photos && !failedImages['photos'] ? (
              <img
                className="w-full h-full object-cover opacity-20 transition-opacity duration-500 ease-out"
                src={backgroundPhotos.photos}
                alt="Photos background"
                onError={() => handleImageError('photos')}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-white to-ivory-50 dark:from-gray-800 dark:to-gray-900 transition-colors duration-500 ease-out"></div>
            )}
            <div className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 transition-opacity duration-500 ease-out"></div>
          </motion.div>
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="text-center">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="text-4xl font-playfair text-gray-900 dark:text-white"
              >
                Our Moments
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-4 text-lg font-lora text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
              >
                Cherished memories captured in time
              </motion.p>
            </div>

            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {featuredPhotos.slice(0, 6).map((photo, index) => (
                <motion.div
                  key={photo._id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.03, transition: { duration: 0.3 } }}
                  className="relative rounded-2xl shadow-xl overflow-hidden"
                >
                  <div className="relative h-64">
                    {failedImages[photo._id] ? (
                      <div className="h-full w-full flex items-center justify-center bg-gray-100">
                        <div className="text-center p-4">
                          <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Image unavailable</p>
                        </div>
                      </div>
                    ) : (
                      <img
                        className="h-full w-full object-cover transition-transform duration-300 ease-out"
                        src={getDisplayUrl(photo.url)}
                        alt={photo.title || 'Wedding photo'}
                        onError={() => handleImageError(photo._id)}
                        loading="lazy"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 ease-out"></div>
                  </div>
                  {(photo.title || photo.caption) && (
                    <div className="p-6 bg-white dark:bg-gray-700">
                      {photo.title && (
                        <h3 className="text-lg font-playfair text-gray-900 dark:text-white">{photo.title}</h3>
                      )}
                      {photo.caption && (
                        <p className="mt-2 text-sm font-lora text-gray-600 dark:text-gray-300">{photo.caption}</p>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to={`/wedding/${customUrl}/photos`}
                className="inline-flex items-center px-6 py-3 bg-rose-500 text-white font-lora rounded-full shadow-md hover:bg-rose-600 transition-colors duration-300 ease-out"
              >
                View All Photos
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </motion.div>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="relative py-24 px-4 sm:px-6 lg:px-8"
        >
          <motion.div className="absolute inset-0 z-0" style={{ y: eventsY }}>
            {backgroundPhotos.events && !failedImages['events'] ? (
              <img
                className="w-full h-full object-cover transition-opacity duration-500 ease-out"
                src={backgroundPhotos.events}
                alt="Events background"
                onError={() => handleImageError('events')}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-rose-900 to-navy-900 transition-colors duration-500 ease-out"></div>
            )}
            <div className="absolute inset-0 bg-gradient-to-br from-rose-900/70 to-navy-900/70 transition-opacity duration-500 ease-out"></div>
          </motion.div>
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="text-center">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="text-4xl font-playfair text-white"
              >
                Our Celebrations
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-4 text-lg font-lora text-ivory-100 max-w-2xl mx-auto"
              >
                Join us for these unforgettable moments
              </motion.p>
            </div>

            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {upcomingEvents.slice(0, 3).map((event, index) => (
                <motion.div
                  key={event._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                >
                  <Card customClasses="bg-white/95 dark:bg-gray-700/95 backdrop-blur-sm rounded-2xl shadow-xl">
                    <div className="flex items-start">
                      <div className="bg-rose-500 rounded-lg p-3 text-center min-w-[3.5rem]">
                        <span className="text-xs font-lora text-white uppercase">
                          {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                        <p className="text-xl font-playfair text-white">
                          {new Date(event.date).getDate()}
                        </p>
                      </div>
                      <div className="ml-4 space-y-2">
                        <h3 className="text-lg font-playfair text-gray-900 dark:text-white">{event.title}</h3>
                        {event.time && (
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">Time:</span> {event.time}
                          </p>
                        )}
                        {event.location && (
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">Location:</span> {event.location}
                          </p>
                        )}
                        {event.address && (
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">Address:</span> {event.address}
                          </p>
                        )}
                        {event.description && (
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{event.description}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {upcomingEvents.length > 3 && (
              <div className="mt-12 text-center">
                <Link
                  to={`/wedding/${customUrl}/events`}
                  className="inline-flex items-center px-6 py-3 bg-white text-rose-500 font-lora rounded-full shadow-md hover:bg-rose-50 transition-colors duration-300 ease-out"
                >
                  View All Events
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative py-24 px-4 sm:px-6 lg:px-8 mb-16"
      >
        <motion.div className="absolute inset-0 z-0" style={{ y: ctaY }}>
          {backgroundPhotos.cta && !failedImages['cta'] ? (
            <img
              className="w-full h-full object-cover transition-opacity duration-500 ease-out"
              src={backgroundPhotos.cta}
              alt="Call to Action background"
              onError={() => handleImageError('cta')}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-rose-900 to-navy-900 transition-colors duration-500 ease-out"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-rose-900/80 to-navy-900/80 transition-opacity duration-500 ease-out"></div>
        </motion.div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-4xl font-playfair text-white text-center"
          >
            Celebrate With Us
          </motion.h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-1 md:grid-cols-4">
            {[
              {
                icon: Gift,
                title: 'Gift Registry',
                description: 'Help us start our life together with a special gift.',
                link: `/wedding/${customUrl}/registry`,
                delay: 0,
              },
              {
                icon: Calendar,
                title: 'Wedding Events',
                description: 'Explore our schedule and join the festivities.',
                link: `/wedding/${customUrl}/events`,
                delay: 0.2,
              },
              {
                icon: Users,
                title: 'RSVP',
                description: 'Let us know if you’ll be there to share our joy.',
                link: `/wedding/${customUrl}/rsvp`,
                delay: 0.4,
              },
              {
                icon: DollarSign,
                title: 'Contribute',
                description: 'Support our journey with a heartfelt contribution.',
                link: `/wedding/${customUrl}/contribute`,
                delay: 0.6,
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: item.delay }}
                whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
                className="text-center"
              >
                <div className="flex justify-center">
                  <div className="bg-rose-500 rounded-full p-4">
                    <item.icon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="mt-4 text-xl font-playfair text-white">{item.title}</h3>
                <p className="mt-2 text-base font-lora text-ivory-200">{item.description}</p>
                <div className="mt-6">
                  <Link to={item.link}>
                    <Button
                      customClasses="bg-white text-rose-500 font-lora rounded-full px-6 py-3 shadow-md hover:bg-rose-50 transition-colors duration-300 ease-out"
                    >
                      {item.title}
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </VisitorLayout>
  );
};

export default WeddingHome;
